import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import statesData from "../../../data/states.json";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import {
  FaAd,
  FaUsers,
  FaImage,
  FaVideo,
  FaFileAlt,
  FaMapMarkerAlt,
  FaUpload,
  FaRocket,
  FaPlus,
  FaSpinner,
  FaArrowLeft,
  FaArrowRight,
  FaPlay,
  FaPause,
  FaEdit,
  FaHourglassStart
} from "react-icons/fa";

const CreateFreeAds = () => {
  const navigate = useNavigate();
  // Map normalized ad type names to allowed media types
  const adTypeMediaMap = {
    banner: ["image"],
    ltype: ["image"],
    video: ["video"],
  };

  // Helper to normalize ad type names
  const normalizeAdType = (name) =>
    name ? name.toLowerCase().replace(/[^a-z0-9]/gi, "") : "";
  const [adTypes, setAdTypes] = useState([]);
  const [mediaType, setMediaType] = useState("");
  const [description, setDescription] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [allReporters, setAllReporters] = useState([]);
  const [showReporterBox, setShowReporterBox] = useState(false);
  const [showReportersList, setShowReportersList] = useState(false);
  const [selectedReporterIds, setSelectedReporterIds] = useState([]);
  const [selectedInfluencerIds, setSelectedInfluencerIds] = useState([]);
  const [selectedCity, setSelectedCity] = useState("");
  const [cityDropdownOpen, setCityDropdownOpen] = useState(false);
  const [selectedCities, setSelectedCities] = useState([]);
  const [selectAllCities, setSelectAllCities] = useState(false);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAdType, setSelectedAdType] = useState("");
  const [userType, setUserType] = useState("both"); // Default to both
  const [allInfluencers, setAllInfluencers] = useState([]);
  const [showInfluencersList, setShowInfluencersList] = useState(false);
  const [locationUsersCount, setLocationUsersCount] = useState({ reporters: 0, influencers: 0 });

  const cityDropdownRef = useRef(null);

  // Disable logic for location preferences
  const isStateCitySelected = selectedState && selectedCities.length > 0;
  const isAllStateSelected = showReporterBox === true;
  const isReporterSelected = selectedReporterIds.length > 0;
  const isInfluencerSelected = selectedInfluencerIds.length > 0;
  const isUserSelected = isReporterSelected || isInfluencerSelected;
  const disableStateCity = isAllStateSelected || isUserSelected;
  const disableAllState = isStateCitySelected || isUserSelected;
  const disableReporter = isStateCitySelected || isAllStateSelected;

  useEffect(() => {
    // Fetch Ad Types from API
    const fetchAdTypes = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/get/ad/pricing`,
          {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          }
        );
        if (res.data.data.adType) {
          setAdTypes(res.data.data.adType);
        }
      } catch (err) {
        toast.error("Failed to fetch Ad Types");
      }
    };
    fetchAdTypes();
  }, []);

  useEffect(() => {
    // Update cities when state changes
    if (selectedState && statesData[selectedState]) {
      setCities(statesData[selectedState]);
    } else {
      setCities([]);
    }
    setSelectedCity("");
    setSelectedCities([]);
    setSelectAllCities(false);
  }, [selectedState]);

  // ✅ Fetch location users count when state and cities are selected
  useEffect(() => {
    if (selectedState && selectedCities.length > 0) {
      const fetchLocationUsersCount = async () => {
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/admin/get/location/users`,
            {
              headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
              params: {
                state: selectedState,
                cities: selectedCities.join(","),
                userType: "both"
              }
            }
          );
          if (res.data && res.data.success && res.data.data) {
            setLocationUsersCount({
              reporters: res.data.data.reportersCount || 0,
              influencers: res.data.data.influencersCount || 0
            });
          }
        } catch (err) {
          console.error("Error fetching location users count:", err);
          setLocationUsersCount({ reporters: 0, influencers: 0 });
        }
      };
      fetchLocationUsersCount();
    } else {
      setLocationUsersCount({ reporters: 0, influencers: 0 });
    }
  }, [selectedState, selectedCities]);

  // ✅ Fetch users based on location when showReportersList is true
  useEffect(() => {
    if (showReportersList && selectedState && selectedCities.length > 0) {
      const fetchLocationReporters = async () => {
        setLoading(true);
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/admin/get/location/users`,
            {
              headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
              params: {
                state: selectedState,
                cities: selectedCities.join(","),
                userType: userType === "both" ? "reporter" : userType
              }
            }
          );
          if (res.data && res.data.success && res.data.data) {
            const locationUsers = res.data.data.users || [];
            const reporters = locationUsers.filter(user => user.role === "Reporter");
            setAllReporters(reporters);
          } else {
            setAllReporters([]);
          }
        } catch (err) {
          setAllReporters([]);
          toast.error("Failed to fetch reporters for selected location");
        } finally {
          setLoading(false);
        }
      };
      fetchLocationReporters();
    } else if (showReportersList) {
      setAllReporters([]);
      toast.error("Please select state and cities first to see reporters");
    } else {
      setAllReporters([]);
      setSelectedReporterIds([]);
    }
  }, [showReportersList, selectedState, selectedCities, userType]);

  // ✅ Fetch influencers based on location when showInfluencersList is true
  useEffect(() => {
    if (showInfluencersList && selectedState && selectedCities.length > 0) {
      const fetchLocationInfluencers = async () => {
        setLoading(true);
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/admin/get/location/users`,
            {
              headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
              params: {
                state: selectedState,
                cities: selectedCities.join(","),
                userType: userType === "both" ? "influencer" : userType
              }
            }
          );
          if (res.data && res.data.success && res.data.data) {
            const locationUsers = res.data.data.users || [];
            const influencers = locationUsers.filter(user => user.role === "Influencer");
            setAllInfluencers(influencers);
          } else {
            setAllInfluencers([]);
          }
        } catch (err) {
          setAllInfluencers([]);
          toast.error("Failed to fetch influencers for selected location");
        } finally {
          setLoading(false);
        }
      };
      fetchLocationInfluencers();
    } else if (showInfluencersList) {
      setAllInfluencers([]);
      toast.error("Please select state and cities first to see influencers");
    } else {
      setAllInfluencers([]);
      setSelectedInfluencerIds([]);
    }
  }, [showInfluencersList, selectedState, selectedCities, userType]);

  const [mediaFile, setMediaFile] = useState(null);
  const [mediaFileUrl, setMediaFileUrl] = useState(null);

  useEffect(() => {
    if (mediaFile) {
      const url = URL.createObjectURL(mediaFile);
      setMediaFileUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    }
  }, [mediaFile]);

  const handleAdTypeChange = (e) => {
    const newAdType = e.target.value;
    setSelectedAdType(newAdType);
    const normType = normalizeAdType(newAdType);
    const allowedMedia = adTypeMediaMap[normType] || [];
    setMediaType(allowedMedia.length === 1 ? allowedMedia[0] : "");
    setMediaFile(null);
  };
  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    setMediaFile(selected);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (selectedState && selectedCities.length === 0) {
      toast.error("Please select at least one city for the selected state.");
      setLoading(false);
      return;
    }

    const isLocationSelected = (showReporterBox === true) || (selectedState && selectedCities.length > 0) || selectedReporterIds.length > 0 || selectedInfluencerIds.length > 0;
    if (!isLocationSelected) {
      toast.error("Please select at least one: State & City, All State checkbox, or User selection.");
      setLoading(false);
      return;
    }

    if (!mediaFile) {
      toast.error("Please select a media file before submitting.");
      setLoading(false);
      return;
    }

    try {
      const formData = new FormData();
      formData.append("adType", e.target.adType.value);
      formData.append("mediaType", mediaType);

      if (mediaType === "video") {
        formData.append("video", mediaFile);
      } else {
        formData.append("image", mediaFile);
      }

      formData.append("description", description);
      formData.append("state", selectedState);
      formData.append("cities", selectedCities.join(","));
      formData.append("reportersIds", selectedReporterIds.join(","));
      formData.append("influencersIds", selectedInfluencerIds.join(","));
      formData.append("userType", userType);

      if (showReporterBox) {
        formData.append("allStatesTrue", true);
      }

      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/free/ads`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (res.status >= 200 && res.status < 300) {
        toast.success("Free ad posted successfully! Notifications are being sent in the background.");
        setTimeout(() => {
          window.location.href = "/admin/freependingads";
        }, 1000);
        setMediaFile(null);
        setDescription("");
        setSelectedState("");
        setSelectedCity("");
        setSelectedCities([]);
        setSelectAllCities(false);
        setMediaType("");
        setSelectedReporterIds([]);
        setSelectedInfluencerIds([]);
        setShowReportersList(false);
        setShowInfluencersList(false);
      } else if (res.data && res.data.message) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data?.message || "Failed to post ad");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to post ad");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!cityDropdownOpen) return;
    const handleClickOutside = (event) => {
      if (
        cityDropdownRef.current &&
        !cityDropdownRef.current.contains(event.target)
      ) {
        setCityDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [cityDropdownOpen]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/50 pb-12">
      <ToastContainer position="top-center" autoClose={3000} theme="dark" />

      {/* Premium Header */}
      <div className="max-w-5xl mx-auto px-2 sm:px-4 py-4 sm:py-8">
        <div className="bg-gradient-to-br from-violet-600 via-purple-700 to-indigo-800 rounded-[2.5rem] shadow-2xl p-6 sm:p-10 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -mr-32 -mt-32" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-purple-400/10 rounded-full blur-2xl -ml-24 -mb-24" />

          <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
            <div className="flex items-center gap-4 sm:gap-6">
              <div className="p-4 sm:p-5 bg-white/15 rounded-[2rem] backdrop-blur-md shadow-inner">
                <FaRocket className="text-2xl sm:text-4xl text-yellow-300 animate-pulse" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-4xl font-black tracking-tight leading-none uppercase">Create Reward Ad</h1>
                <p className="text-purple-100 mt-2 text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] opacity-80">Launch New Marketing Campaign</p>
              </div>
            </div>
            <button
              onClick={() => navigate("/admin/freependingads")}
              className="bg-white/10 hover:bg-white/20 border border-white/20 px-6 py-3 rounded-2xl transition-all font-black text-xs backdrop-blur-sm self-start sm:self-center"
            >
              VIEW ALL ADS
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-2 sm:px-4">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

            {/* Left Column: Configuration */}
            <div className="space-y-6">
              {/* Campaign Setup Card */}
              <div className="bg-white rounded-[2rem] shadow-xl shadow-slate-200/50 border border-slate-100 p-6 sm:p-8 relative group hover:border-violet-200 transition-colors">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-10 h-10 bg-violet-50 text-violet-600 rounded-xl flex items-center justify-center font-black shadow-sm">
                    <FaAd />
                  </div>
                  <h2 className="text-sm font-black text-slate-800 uppercase tracking-widest">Campaign Setup</h2>
                </div>

                <div className="space-y-6">
                  <div className="group/field">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block group-focus-within/field:text-violet-600 transition-colors">Select Ad Type</label>
                    <select
                      name="adType"
                      className="w-full bg-slate-50 border-0 rounded-2xl p-4 text-sm font-bold text-slate-700 focus:ring-4 focus:ring-violet-500/10 outline-none transition-all appearance-none cursor-pointer"
                      required
                      value={selectedAdType}
                      onChange={handleAdTypeChange}
                    >
                      <option value="">Choose Format</option>
                      {adTypes.map((type) => (
                        <option key={type.id} value={type.name}>{type.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 block">Target Audience</label>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { val: 'reporter', label: 'Reporters', icon: FaUsers, color: 'blue' },
                        { val: 'influencer', label: 'Influencers', icon: FaUsers, color: 'purple' },
                        { val: 'both', label: 'Universal', icon: FaPlus, color: 'emerald' }
                      ].map((type) => (
                        <button
                          key={type.val}
                          type="button"
                          onClick={() => setUserType(type.val)}
                          className={`flex flex-col items-center justify-center p-3 rounded-2xl border-2 transition-all gap-2 ${userType === type.val
                            ? `bg-slate-900 border-slate-900 text-white shadow-lg scale-105`
                            : 'bg-white border-slate-100 text-slate-400 hover:border-slate-200'}`}
                        >
                          <type.icon className="text-sm" />
                          <span className="text-[9px] font-black uppercase tracking-tighter">{type.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Geo-Targeting Card */}
              <div className="bg-white rounded-[2rem] shadow-xl shadow-slate-200/50 border border-slate-100 p-6 sm:p-8 relative group hover:border-orange-200 transition-colors">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-orange-50 text-orange-600 rounded-xl flex items-center justify-center font-black shadow-sm">
                      <FaMapMarkerAlt />
                    </div>
                    <h2 className="text-sm font-black text-slate-800 uppercase tracking-widest">Geo-Targeting</h2>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer bg-slate-100 px-3 py-1.5 rounded-full hover:bg-slate-200 transition-colors">
                    <input
                      type="checkbox"
                      checked={showReporterBox}
                      onChange={(e) => setShowReporterBox(e.target.checked)}
                      className="rounded-full text-orange-500 w-4 h-4 border-none bg-white focus:ring-0"
                      disabled={disableAllState}
                    />
                    <span className="text-[9px] font-black text-slate-600 uppercase">National</span>
                  </label>
                </div>

                <div className="space-y-6">
                  <div className={`transition-opacity ${showReporterBox ? 'opacity-40 pointer-events-none' : 'opacity-100'}`}>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block">Target Region</label>
                    <select
                      className="w-full bg-slate-50 border-0 rounded-2xl p-4 text-sm font-bold text-slate-700 focus:ring-4 focus:ring-orange-500/10 outline-none transition-all appearance-none cursor-pointer"
                      value={selectedState}
                      onChange={(e) => setSelectedState(e.target.value)}
                      disabled={disableStateCity}
                    >
                      <option value="">Select State</option>
                      {Object.keys(statesData).map((state) => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>

                  <div className={`transition-opacity ${!selectedState || showReporterBox ? 'opacity-40 pointer-events-none' : 'opacity-100'}`}>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block">Focus Cities</label>
                    <div className="relative" ref={cityDropdownRef}>
                      <button
                        type="button"
                        className="w-full bg-slate-50 border-0 rounded-2xl p-4 text-sm font-bold text-slate-700 flex items-center justify-between hover:bg-slate-100 transition-all"
                        onClick={() => setCityDropdownOpen(!cityDropdownOpen)}
                      >
                        <span className="truncate">{selectedCities.length === 0 ? "Pick Locations" : `${selectedCities.length} Selected`}</span>
                        <FaPlus className={`text-xs transition-transform ${cityDropdownOpen ? 'rotate-45' : ''}`} />
                      </button>

                      {cityDropdownOpen && (
                        <div className="absolute z-50 w-full mt-3 bg-white border border-slate-100 rounded-[1.5rem] shadow-2xl p-3 max-h-60 overflow-y-auto animate-zoomIn">
                          <label className="flex items-center p-3 hover:bg-slate-50 rounded-xl cursor-pointer sticky top-0 bg-white border-b border-slate-50 z-10">
                            <input
                              type="checkbox"
                              checked={selectAllCities}
                              onChange={(e) => {
                                setSelectAllCities(e.target.checked);
                                setSelectedCities(e.target.checked ? [...cities] : []);
                              }}
                              className="rounded-full text-orange-500 w-5 h-5 border-slate-200 focus:ring-0 mr-3"
                            />
                            <span className="text-xs font-black uppercase text-slate-800">Select All Cities</span>
                          </label>
                          <div className="mt-2 space-y-1">
                            {cities.map((city) => (
                              <label key={city} className="flex items-center p-3 hover:bg-slate-50 rounded-xl cursor-pointer">
                                <input
                                  type="checkbox"
                                  value={city}
                                  checked={selectedCities.includes(city)}
                                  onChange={(e) => {
                                    const updated = e.target.checked ? [...selectedCities, city] : selectedCities.filter((c) => c !== city);
                                    setSelectedCities(updated);
                                    setSelectAllCities(updated.length === cities.length);
                                  }}
                                  className="rounded-full text-orange-500 w-5 h-5 border-slate-200 focus:ring-0 mr-3"
                                />
                                <span className="text-xs font-bold text-slate-600">{city}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {selectedCities.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-4 max-h-24 overflow-y-auto p-1">
                        {selectedCities.map((city) => (
                          <span key={city} className="bg-slate-900 text-white pl-3 pr-2 py-1.5 rounded-xl text-[10px] font-black uppercase flex items-center gap-2 shadow-sm animate-fadeIn">
                            {city}
                            <button
                              type="button"
                              onClick={() => {
                                const updated = selectedCities.filter((c) => c !== city);
                                setSelectedCities(updated);
                                setSelectAllCities(updated.length === cities.length);
                              }}
                              className="hover:text-rose-400 text-sm leading-none"
                            >
                              &times;
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Content */}
            <div className="space-y-6">
              {/* Media Card */}
              <div className="bg-white rounded-[2rem] shadow-xl shadow-slate-200/50 border border-slate-100 p-6 sm:p-8 relative group hover:border-emerald-200 transition-colors">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-10 h-10 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center font-black shadow-sm">
                    <FaUpload />
                  </div>
                  <h2 className="text-sm font-black text-slate-800 uppercase tracking-widest">Creative Assets</h2>
                </div>

                <div className="space-y-6">
                  <div className="flex gap-2">
                    {(adTypeMediaMap[normalizeAdType(selectedAdType)] || ["image", "video"]).map((type) => (
                      <div
                        key={type}
                        className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-2xl border-2 font-black text-[10px] uppercase transition-all ${mediaType === type ? 'bg-emerald-600 border-emerald-600 text-white shadow-lg' : 'bg-slate-50 border-transparent text-slate-400 opacity-50'}`}
                      >
                        {type === "image" ? <FaImage /> : <FaVideo />}
                        {type}
                      </div>
                    ))}
                  </div>

                  <div className={`relative border-2 border-dashed rounded-[2rem] p-6 transition-all min-h-[220px] flex flex-col items-center justify-center text-center ${mediaFile ? 'border-emerald-500 bg-emerald-50/20' : 'border-slate-100 bg-slate-50/50 hover:bg-slate-50 hover:border-slate-200'}`}>
                    {mediaFile ? (
                      <div className="relative w-full group/media">
                        <button
                          type="button"
                          className="absolute -top-4 -right-4 bg-rose-500 text-white rounded-2xl w-10 h-10 flex items-center justify-center shadow-xl hover:scale-110 active:scale-95 transition-all z-20"
                          onClick={() => setMediaFile(null)}
                        >
                          &times;
                        </button>
                        {mediaType === "video" ? (
                          <video src={mediaFileUrl} className="w-full h-40 object-contain rounded-2xl bg-black shadow-2xl" />
                        ) : (
                          <img src={mediaFileUrl} alt="Preview" className="w-full h-40 object-contain rounded-2xl shadow-2xl" />
                        )}
                        <div className="bg-white/80 backdrop-blur-sm mt-4 px-4 py-2 rounded-xl border border-slate-100 inline-block">
                          <p className="text-[10px] font-black text-slate-600 truncate max-w-[200px] uppercase tracking-tighter">{mediaFile.name}</p>
                        </div>
                      </div>
                    ) : (
                      <label className="cursor-pointer w-full group/upload">
                        <div className="w-16 h-16 bg-white rounded-3xl flex items-center justify-center mb-4 mx-auto shadow-xl group-hover/upload:scale-110 transition-transform">
                          <FaUpload className="text-violet-500 text-xl" />
                        </div>
                        <p className="text-slate-800 font-black text-xs uppercase tracking-widest mb-2">Drop creative here</p>
                        <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">
                          {mediaType === "video" ? "MP4, AVI • MAX 50MB" : "JPG, PNG • MAX 10MB"}
                        </p>
                        <input
                          type="file"
                          onChange={handleFileChange}
                          accept={mediaType === "video" ? "video/*" : "image/*"}
                          className="hidden"
                          disabled={!mediaType}
                        />
                      </label>
                    )}
                  </div>
                </div>
              </div>

              {/* Description Card */}
              <div className="bg-white rounded-[2rem] shadow-xl shadow-slate-200/50 border border-slate-100 p-6 sm:p-8 relative group hover:border-purple-200 transition-colors">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-purple-50 text-purple-600 rounded-xl flex items-center justify-center font-black shadow-sm">
                    <FaFileAlt />
                  </div>
                  <h2 className="text-sm font-black text-slate-800 uppercase tracking-widest">Ad Copy</h2>
                </div>

                <textarea
                  className="w-full bg-slate-50 border-0 rounded-2xl p-4 text-sm font-bold text-slate-700 focus:ring-4 focus:ring-purple-500/10 outline-none transition-all placeholder:text-slate-300 min-h-[140px]"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Compelling campaign message..."
                  required
                  maxLength={500}
                />
                <div className="flex justify-between items-center mt-3">
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Characters Remaining</span>
                  <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${500 - description.length < 50 ? 'bg-rose-100 text-rose-600' : 'bg-slate-100 text-slate-400'}`}>
                    {500 - description.length}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Action Footer */}
          <div className="pt-8">
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 hover:scale-[1.02] active:scale-[0.98] text-white font-black py-5 px-8 rounded-[2rem] transition-all duration-300 disabled:opacity-50 disabled:hover:scale-100 shadow-2xl shadow-indigo-200 flex items-center justify-center gap-4 uppercase tracking-[0.2em] text-sm"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-[3px] border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Syncing Assets...</span>
                </>
              ) : (
                <>
                  <FaRocket className="text-xl" />
                  <span>Launch Campaign</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateFreeAds;
